import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, FileText, Loader2, Sparkles, Check, X,
  Pencil, Trash2, Building2, Compass, Ruler, Bed,
} from 'lucide-react';
import { HudCard } from '../hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTDOOR_OPTIONS = [
  { value: '', label: '— Aucun —' },
  { value: 'balcony', label: 'Balcon' },
  { value: 'terrace', label: 'Terrasse' },
  { value: 'loggia', label: 'Loggia' },
  { value: 'garden', label: 'Jardin / RDJ' },
];

const ORIENTATION_OPTIONS = ['', 'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

const ApartmentPlanCard = ({ projectId, project, onConfirmed }) => {
  const existing = project?.general_info?.apartment_plan || null;
  const [localSaved, setLocalSaved] = useState(null);  // Optimistic post-confirm state
  const confirmedData = localSaved || existing;
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(existing ? 'confirmed' : 'idle'); // idle | analyzing | review | confirmed
  const [analysis, setAnalysis] = useState(null);
  const [planFileId, setPlanFileId] = useState(existing?.plan_file_id || null);
  const [planPreviewUrl, setPlanPreviewUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Editable fields (review stage)
  const [editSurface, setEditSurface] = useState('');
  const [editTypology, setEditTypology] = useState('');
  const [editRooms, setEditRooms] = useState('');
  const [editOutdoor, setEditOutdoor] = useState('');
  const [editOrientation, setEditOrientation] = useState('');

  const isApartment = project?.project_type_code === 'apartment_single';

  const seedEditFields = (a) => {
    setEditSurface(a?.surface_m2 != null ? String(a.surface_m2) : '');
    setEditTypology(a?.typology || '');
    setEditRooms(a?.rooms_count != null ? String(a.rooms_count) : '');
    setEditOutdoor(a?.outdoor_space || '');
    setEditOrientation(a?.orientation || '');
  };

  const handleFiles = useCallback(async (file) => {
    if (!file) return;
    const okTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      toast.error('Format non supporté. Acceptés : PDF, PNG, JPEG, WEBP.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 25 Mo).');
      return;
    }
    setBusy(true);
    setStage('analyzing');
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await axios.post(
        `${API}/projects/${projectId}/apartment-plan/analyze`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setAnalysis(resp.data.analysis);
      setPlanFileId(resp.data.plan_file_id);
      setPlanPreviewUrl(`${process.env.REACT_APP_BACKEND_URL}${resp.data.plan_file_url}`);
      seedEditFields(resp.data.analysis);
      setStage('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyse IA échouée');
      setStage('idle');
    } finally { setBusy(false); }
  }, [projectId]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFiles(f);
  };
  const onPickClick = () => fileInputRef.current?.click();
  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFiles(f);
    e.target.value = ''; // allow re-pick of same file
  };

  const handleConfirm = async () => {
    if (!planFileId) return;
    setBusy(true);
    try {
      const surfaceNum = editSurface ? Number(editSurface) : null;
      const roomsNum = editRooms ? Number(editRooms) : null;
      const payload = {
        plan_file_id: planFileId,
        surface_m2: Number.isFinite(surfaceNum) ? surfaceNum : null,
        typology: editTypology.trim().toUpperCase() || null,
        rooms_count: Number.isFinite(roomsNum) ? roomsNum : null,
        outdoor_space: editOutdoor || null,
        orientation: editOrientation || null,
      };
      const resp = await axios.post(
        `${API}/projects/${projectId}/apartment-plan/confirm`,
        payload,
        { withCredentials: true },
      );
      toast.success('Plan validé et enregistré ✓');
      // Promote the just-saved data into the "confirmed" view immediately so
      // the user sees the recap without a page reload.
      const saved = resp.data?.general_info?.apartment_plan;
      if (saved) {
        setLocalSaved(saved);
        // Make sure the preview URL is set (the just-uploaded file)
        if (!planPreviewUrl) {
          setPlanPreviewUrl(`${API}/projects/${projectId}/apartment-plan/${saved.plan_file_id}/preview`);
        }
      }
      setStage('confirmed');
      onConfirmed?.(resp.data.general_info);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'enregistrement');
    } finally { setBusy(false); }
  };

  const handleRetake = () => {
    setStage('idle');
    setAnalysis(null);
    setPlanFileId(null);
    setPlanPreviewUrl(null);
    setLocalSaved(null);
  };

  // Re-seed when project loads with existing data
  useEffect(() => {
    if (existing) {
      seedEditFields(existing);
      setStage('confirmed');
      setPlanFileId(existing.plan_file_id);
      if (existing.plan_file_id) {
        setPlanPreviewUrl(`${API}/projects/${projectId}/apartment-plan/${existing.plan_file_id}/preview`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.plan_file_id]);

  if (!isApartment) return null;

  return (
    <HudCard variant="glass" bracket data-testid="apartment-plan-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Plan de l'appartement</span>
          <p
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
          >
            {stage === 'confirmed'
              ? 'Données validées'
              : 'Analyse intelligente du plan par Gemini Vision'}
          </p>
          {stage !== 'confirmed' && (
            <p className="text-[11.5px] mt-1" style={{ color: 'var(--c-text-muted)' }}>
              Glissez votre plan (PDF/PNG) — l'IA pré-extrait surface, typologie, espace extérieur…
              Vous validez ensuite manuellement.
            </p>
          )}
        </div>
        <Sparkles className="w-5 h-5" style={{ color: 'var(--c-gold)' }} />
      </div>

      {/* ===== STAGE: IDLE — Drop zone ===== */}
      {stage === 'idle' && (
        <div
          onClick={onPickClick}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          data-testid="apartment-plan-dropzone"
          className="cursor-pointer rounded-xl px-6 py-10 flex flex-col items-center justify-center text-center transition-all"
          style={{
            background: dragActive ? 'rgba(217,160,91,0.10)' : 'rgba(255,255,255,0.03)',
            border: `1.5px dashed ${dragActive ? 'var(--c-gold)' : 'rgba(255,255,255,0.18)'}`,
          }}
        >
          <UploadCloud
            className="w-10 h-10 mb-3"
            style={{ color: dragActive ? 'var(--c-gold)' : 'rgba(255,255,255,0.55)' }}
          />
          <p className="text-[13px] font-semibold" style={{ color: 'var(--c-text)' }}>
            Glissez votre plan ici, ou cliquez pour parcourir
          </p>
          <p className="text-[11px] mt-1.5" style={{ color: 'var(--c-text-muted)' }}>
            PDF · PNG · JPEG · WEBP — max 25 Mo
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onInputChange}
            data-testid="apartment-plan-file-input"
          />
        </div>
      )}

      {/* ===== STAGE: ANALYZING — Loader ===== */}
      {stage === 'analyzing' && (
        <div
          className="rounded-xl px-6 py-10 flex flex-col items-center justify-center text-center"
          style={{
            background: 'rgba(217,160,91,0.05)',
            border: '1px solid rgba(217,160,91,0.22)',
          }}
          data-testid="apartment-plan-analyzing"
        >
          <Loader2 className="w-10 h-10 mb-3 animate-spin" style={{ color: 'var(--c-gold)' }} />
          <p className="text-[13px] font-semibold" style={{ color: 'var(--c-text)' }}>
            Gemini Vision analyse votre plan…
          </p>
          <p className="text-[11px] mt-1.5" style={{ color: 'var(--c-text-muted)' }}>
            Quelques secondes seulement.
          </p>
        </div>
      )}

      {/* ===== STAGE: REVIEW — Editable AI suggestion ===== */}
      {stage === 'review' && (
        <ReviewBlock
          previewUrl={planPreviewUrl}
          analysis={analysis}
          editSurface={editSurface} setEditSurface={setEditSurface}
          editTypology={editTypology} setEditTypology={setEditTypology}
          editRooms={editRooms} setEditRooms={setEditRooms}
          editOutdoor={editOutdoor} setEditOutdoor={setEditOutdoor}
          editOrientation={editOrientation} setEditOrientation={setEditOrientation}
          busy={busy}
          onConfirm={handleConfirm}
          onRetake={handleRetake}
        />
      )}

      {/* ===== STAGE: CONFIRMED — Compact recap ===== */}
      {stage === 'confirmed' && confirmedData && (
        <ConfirmedBlock
          previewUrl={planPreviewUrl}
          data={confirmedData}
          onEdit={() => {
            seedEditFields(confirmedData);
            setStage('review');
            setAnalysis(confirmedData);
          }}
          onReplace={handleRetake}
        />
      )}
    </HudCard>
  );
};

// ==================== SUB-COMPONENTS ====================

const ReviewBlock = ({
  previewUrl, analysis,
  editSurface, setEditSurface,
  editTypology, setEditTypology,
  editRooms, setEditRooms,
  editOutdoor, setEditOutdoor,
  editOrientation, setEditOrientation,
  busy, onConfirm, onRetake,
}) => (
  <div className="space-y-4" data-testid="apartment-plan-review">
    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
      {/* Plan preview */}
      <div
        className="rounded-lg overflow-hidden flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.45)', minHeight: 180 }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Plan analysé"
            className="w-full h-full object-contain"
            crossOrigin="use-credentials"
          />
        ) : (
          <FileText className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.30)' }} />
        )}
      </div>

      {/* Editable form */}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Surface habitable"
          unit="m²"
          icon={Ruler}
          value={editSurface}
          onChange={setEditSurface}
          type="number"
          step="0.1"
          placeholder="—"
          aiValue={analysis?.surface_m2}
          testid="apt-edit-surface"
        />
        <Field
          label="Typologie"
          icon={Building2}
          value={editTypology}
          onChange={(v) => setEditTypology(v.toUpperCase())}
          placeholder="T2"
          maxLength={3}
          aiValue={analysis?.typology}
          testid="apt-edit-typology"
        />
        <Field
          label="Pièces principales"
          icon={Bed}
          value={editRooms}
          onChange={setEditRooms}
          type="number"
          placeholder="—"
          aiValue={analysis?.rooms_count}
          testid="apt-edit-rooms"
        />
        <FieldSelect
          label="Espace extérieur"
          value={editOutdoor}
          onChange={setEditOutdoor}
          options={OUTDOOR_OPTIONS}
          aiValue={analysis?.outdoor_space}
          testid="apt-edit-outdoor"
        />
        <FieldSelect
          label="Orientation"
          icon={Compass}
          value={editOrientation}
          onChange={setEditOrientation}
          options={ORIENTATION_OPTIONS.map((v) => ({ value: v, label: v || '— Non détectée —' }))}
          aiValue={analysis?.orientation}
          testid="apt-edit-orientation"
        />
      </div>
    </div>

    {analysis?.confidence != null && (
      <div
        className="rounded-lg px-3 py-2 flex items-start gap-2 text-[11px]"
        style={{
          background: analysis.confidence >= 0.7 ? 'rgba(34,197,94,0.07)' : 'rgba(253,224,71,0.07)',
          border: `1px solid ${analysis.confidence >= 0.7 ? 'rgba(34,197,94,0.22)' : 'rgba(253,224,71,0.22)'}`,
          color: 'rgba(255,255,255,0.75)',
        }}
        data-testid="apartment-plan-confidence"
      >
        <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5"
          style={{ color: analysis.confidence >= 0.7 ? '#86efac' : '#FDE68A' }} />
        <span>
          <strong>Confiance IA : {Math.round(analysis.confidence * 100)}%</strong>
          {analysis.reasoning && <> · {analysis.reasoning}</>}
        </span>
      </div>
    )}

    <div className="flex items-center justify-between gap-2 pt-2"
      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <button
        type="button"
        onClick={onRetake}
        disabled={busy}
        data-testid="apartment-plan-retake"
        className="text-[12px] flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/[0.06] transition-colors"
        style={{ color: 'rgba(255,255,255,0.65)' }}
      >
        <X className="w-3.5 h-3.5" /> Choisir un autre plan
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy}
        data-testid="apartment-plan-confirm"
        className="px-5 py-2.5 rounded-lg flex items-center gap-2 font-semibold transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
        style={{
          background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
          color: '#0E0E10',
          fontFamily: 'Outfit, sans-serif',
          fontSize: '13px',
          boxShadow: '0 4px 14px -4px rgba(217,160,91,0.45)',
        }}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
        Confirmer & enregistrer
      </button>
    </div>
  </div>
);

const ConfirmedBlock = ({ previewUrl, data, onEdit, onReplace }) => {
  const outdoor = OUTDOOR_OPTIONS.find((o) => o.value === data.outdoor_space)?.label;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4" data-testid="apartment-plan-confirmed">
      <div
        className="rounded-lg overflow-hidden flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.45)', minHeight: 180 }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Plan"
            className="w-full h-full object-contain"
            crossOrigin="use-credentials"
          />
        ) : (
          <FileText className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.30)' }} />
        )}
      </div>
      <div className="space-y-2.5">
        <KV label="Surface habitable" value={data.surface_m2 != null ? `${data.surface_m2} m²` : '—'} icon={Ruler} />
        <KV label="Typologie" value={data.typology || '—'} icon={Building2} />
        <KV label="Pièces principales" value={data.rooms_count != null ? `${data.rooms_count}` : '—'} icon={Bed} />
        <KV label="Espace extérieur" value={outdoor || '—'} />
        <KV label="Orientation" value={data.orientation || '—'} icon={Compass} />

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onEdit}
            data-testid="apartment-plan-edit"
            className="text-[11.5px] flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/[0.06] transition-colors"
            style={{ color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.30)' }}
          >
            <Pencil className="w-3 h-3" /> Modifier
          </button>
          <button
            type="button"
            onClick={onReplace}
            data-testid="apartment-plan-replace"
            className="text-[11.5px] flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/[0.06] transition-colors"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            <Trash2 className="w-3 h-3" /> Remplacer le plan
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, unit, icon: Icon, value, onChange, type = 'text', step, placeholder, maxLength, aiValue, testid }) => {
  const aiHint = aiValue != null && aiValue !== '' && String(aiValue) !== String(value);
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold mb-1.5 flex items-center gap-1"
        style={{ color: 'rgba(255,255,255,0.55)' }}
      >
        {Icon && <Icon className="w-3 h-3" />}{label}
      </label>
      <div className="relative">
        <input
          type={type}
          step={step}
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full h-9 px-2.5 pr-9 rounded-md bg-white/5 border border-white/15 text-white text-[13px] placeholder:text-white/30 focus:border-[#D9A05B] outline-none"
        />
        {unit && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10.5px]"
            style={{ color: 'rgba(255,255,255,0.40)' }}
          >
            {unit}
          </span>
        )}
      </div>
      {aiHint && (
        <p className="text-[9.5px] mt-1 flex items-center gap-1" style={{ color: 'rgba(217,160,91,0.65)' }}>
          <Sparkles className="w-2.5 h-2.5" /> IA : {String(aiValue)}
        </p>
      )}
    </div>
  );
};

const FieldSelect = ({ label, icon: Icon, value, onChange, options, aiValue, testid }) => {
  const aiHint = aiValue && aiValue !== value;
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold mb-1.5 flex items-center gap-1"
        style={{ color: 'rgba(255,255,255,0.55)' }}
      >
        {Icon && <Icon className="w-3 h-3" />}{label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-9 px-2.5 rounded-md bg-white/5 border border-white/15 text-white text-[13px] focus:border-[#D9A05B] outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ background: '#1a1a1f' }}>
            {opt.label}
          </option>
        ))}
      </select>
      {aiHint && (
        <p className="text-[9.5px] mt-1 flex items-center gap-1" style={{ color: 'rgba(217,160,91,0.65)' }}>
          <Sparkles className="w-2.5 h-2.5" /> IA : {aiValue}
        </p>
      )}
    </div>
  );
};

const KV = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between text-[12.5px]">
    <span className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
      {Icon && <Icon className="w-3 h-3" />}{label}
    </span>
    <span className="font-semibold" style={{ color: 'var(--c-text)' }}>{value}</span>
  </div>
);

export default ApartmentPlanCard;
